import type { Remito } from '../../api';

/**
 * El remito, dibujado en papel A4.
 *
 * **Las medidas salen del PDF del formulario que RDM ya usa**, no de un
 * parecido: se leyeron las coordenadas de cada texto y de cada trazo y están
 * puestas acá tal cual, en milímetros desde el borde de la hoja. Quien recibe
 * la mercadería del otro lado compara contra el papel de siempre, así que
 * «parecido» no sirve.
 *
 * Por eso todo va **posicionado en absoluto**: un layout en flujo se acomoda
 * solo y se corre medio milímetro en cuanto cambia un texto, y acá el lugar de
 * cada cosa es un dato medido, no una consecuencia. Las coordenadas son las
 * del PDF: `y` se cuenta desde arriba y los textos se ubican por su **línea de
 * base**, que es como las da el PDF, para poder compararlas de nuevo el día
 * que el formulario cambie.
 *
 * Todo el estilo va en el propio elemento, nunca en una clase: la hoja se
 * copia con `innerHTML` a la ventana de impresión (ver `lib/imprimir.ts`), y
 * ahí no viaja ninguna hoja de estilos.
 *
 * El componente no sabe de copias: recibe el rótulo y lo escribe. Las copias
 * son esta misma hoja con otro rótulo, que es lo que son en papel.
 */

const NEGRO = '#000';
/** 0,5 pt, que es el trazo del formulario. */
const BORDE = `0.18mm solid ${NEGRO}`;
/** El gris de la barra de la tabla: 0.753 en el PDF. */
const GRIS = '#c0c0c0';

/** Los márgenes del formulario, en mm desde el borde de la hoja. */
const M = {
  izq: 14.4,
  der: 203.5,
  /** Encabezado: la caja de arriba, con la copia, el logo y el cliente. */
  encabezado: { y: 5.8, franja: 15.8, cliente: 66.3, y2: 88.1 },
  /** El recuadro del medio: el número del remito. */
  pestana: { x: 101.2, x2: 118.6, y2: 30.9 },
  /** El divisor entre el logo y la fecha, debajo del recuadro. */
  divisor: 110.8,
  // El PDF trae una línea bajo el logo, a 39,5 mm: era el borde de la imagen
  // del logo viejo, no una línea del formulario. No va.
  logo: { x: 16.2, y: 18.2, alto: 23.3 },
  /** La tabla: las cuatro verticales y el alto de cada fila. */
  tabla: { y: 89.7, cols: [14.5, 62.3, 182.0, 203.1], fila: 4.1 },
  /** Los tres recuadros del pie, separados entre sí como en el formulario. */
  transporte: { x: 14.6, x2: 203.9, y: 219.0, y2: 236.7 },
  observaciones: { x: 14.4, x2: 203.5, y: 238.5, y2: 251.2 },
  facturas: { x: 14.3, x2: 203.2, y: 253.1, y2: 264.4 },
  /** El texto de las observaciones: dónde arranca, su cuerpo y su interlínea. */
  obs: { x: 16.2, base: 246.3, cuerpo: 8, interlinea: 1.25 },
} as const;

const ANCHO_MM = 210;
const ALTO_MM = 297;

/** Un punto en milímetros: la unidad en la que vienen los cuerpos del PDF. */
const pt = (n: number) => n * (25.4 / 72);

/**
 * Cuántos renglones entran en la tabla antes de pisar el recuadro del
 * transporte: el encabezado gris y cada renglón miden `fila`. No hay segunda
 * hoja, así que el formulario no deja emitir uno más largo.
 */
export const RENGLONES_MAX = Math.floor((M.transporte.y - M.tabla.y - M.tabla.fila) / M.tabla.fila);

// ponytail: el ancho de una letra se estima en 0,6 del cuerpo (Arial, mayúsculas
// y números). Con minúsculas entra algo más; si hace falta exactitud, medir el
// texto con `canvas.measureText`.
const letra = (cuerpo: number) => pt(cuerpo) * 0.6;

/** Cuántas letras entran en la columna Producto antes de que se corte. */
export const LETRAS_PRODUCTO = Math.floor((M.tabla.cols[2] - M.tabla.cols[1] - 1.6) / letra(7));

/** Cuántas letras entran en un renglón de las observaciones. */
const LETRAS_OBSERVACION = Math.floor((M.observaciones.x2 - M.obs.x - 2) / letra(M.obs.cuerpo));
const topeObs = M.obs.base - pt(M.obs.cuerpo) * 0.8;

/** Cuántos renglones de observaciones entran dentro de su recuadro. */
export const LINEAS_OBSERVACIONES = Math.floor(
  (M.observaciones.y2 - topeObs) / (pt(M.obs.cuerpo) * M.obs.interlinea),
);

/** Los renglones que ocupan unas observaciones: los saltos y lo que se parte por largo. */
export const lineasObservaciones = (texto: string) =>
  texto.split('\n').reduce((n, l) => n + Math.max(1, Math.ceil(l.length / LETRAS_OBSERVACION)), 0);

/**
 * Un largo en milímetros, redondeado a centésimas. Restar coordenadas deja
 * colas de punto flotante —`15.099999999999998mm`— que el navegador entiende
 * igual pero que ensucian el HTML que se copia a la ventana de impresión y
 * hacen ilegible cualquier comparación contra las medidas del formulario.
 */
const mm = (n: number) => `${Math.round(n * 100) / 100}mm`;

/**
 * Un texto puesto donde lo pone el PDF.
 *
 * `base` es la línea de base, que es la coordenada que da el PDF. El navegador
 * posiciona por el borde de arriba de la caja, así que se le descuenta el
 * ascenso de la fuente —0,8 del cuerpo en las de palo seco— y se fija
 * `line-height: 1` para que no lo corra nada más.
 */
function T({
  x,
  base,
  cuerpo,
  negrita,
  ancho,
  alinear,
  children,
}: {
  x: number;
  base: number;
  cuerpo: number;
  negrita?: boolean;
  ancho?: number;
  alinear?: 'left' | 'center' | 'right';
  children: React.ReactNode;
}) {
  return (
    <div
      style={{
        position: 'absolute',
        left: mm(x),
        top: mm(base - pt(cuerpo) * 0.8),
        width: ancho === undefined ? undefined : mm(ancho),
        textAlign: alinear,
        fontSize: `${cuerpo}pt`,
        lineHeight: 1,
        fontWeight: negrita ? 700 : 400,
        whiteSpace: 'pre',
      }}
    >
      {children}
    </div>
  );
}

/** Un rótulo en negrita y su valor al lado, que es como se lee todo el papel. */
function Dato({ x, base, rotulo, valor, salto }: { x: number; base: number; rotulo: string; valor?: string; salto: number }) {
  return (
    <>
      <T x={x} base={base} cuerpo={10} negrita>
        {rotulo}
      </T>
      {valor ? (
        <T x={salto} base={base} cuerpo={9}>
          {valor}
        </T>
      ) : null}
    </>
  );
}

/** Una línea del formulario. Horizontal o vertical, en milímetros. */
function Linea({ x, y, ancho, alto }: { x: number; y: number; ancho?: number; alto?: number }) {
  return (
    <div
      style={{
        position: 'absolute',
        left: mm(x),
        top: mm(y),
        width: ancho === undefined ? '0.18mm' : mm(ancho),
        height: alto === undefined ? '0.18mm' : mm(alto),
        background: NEGRO,
      }}
    />
  );
}

/** Un recuadro del pie: borde de 0,5 pt y nada adentro. */
function Recuadro({ x, x2, y, y2 }: { x: number; x2: number; y: number; y2: number }) {
  return (
    <div
      style={{
        position: 'absolute',
        left: mm(x),
        top: mm(y),
        width: mm(x2 - x),
        height: mm(y2 - y),
        border: BORDE,
        boxSizing: 'border-box',
      }}
    />
  );
}

export type HojaRemitoProps = {
  remito: Remito;
  /** ORIGINAL, DUPLICADO o TRIPLICADO. Es lo único que cambia entre copias. */
  copia: string;
  /** La fecha ya formateada («11/09/2026»): la hoja no hace cuentas. */
  fecha: string;
  /** Las cantidades ya formateadas («1,00»), en el orden de los renglones. */
  cantidades: string[];
};

export default function HojaRemito({ remito, copia, fecha, cantidades }: HojaRemitoProps) {
  const cliente = remito.cliente;
  const [c0, c1, c2, c3] = M.tabla.cols;
  const altoEncabezado = M.encabezado.y2 - M.encabezado.y;

  return (
    <div
      style={{
        position: 'relative',
        width: mm(ANCHO_MM),
        height: mm(ALTO_MM),
        boxSizing: 'border-box',
        overflow: 'hidden',
        background: '#fff',
        color: NEGRO,
        fontFamily: 'Arial, Helvetica, sans-serif',
      }}
    >
      {/* ---------------------------------------------------- encabezado */}
      <div
        style={{
          position: 'absolute',
          left: mm(M.izq),
          top: mm(M.encabezado.y),
          width: mm(M.der - M.izq),
          height: mm(altoEncabezado),
          border: BORDE,
          boxSizing: 'border-box',
        }}
      />
      {/* La franja del rótulo de copia y la línea que la cierra. */}
      <Linea x={M.izq} y={M.encabezado.franja} ancho={M.der - M.izq} />
      <T x={M.izq} base={12.7} cuerpo={15} negrita ancho={M.der - M.izq} alinear="center">
        {copia}
      </T>

      {/* El recuadro del medio va vacío: el número del remito lo pone la hoja
          del contador, no el sistema. */}
      <Linea x={M.pestana.x} y={M.encabezado.franja} alto={M.pestana.y2 - M.encabezado.franja} />
      <Linea x={M.pestana.x2} y={M.encabezado.franja} alto={M.pestana.y2 - M.encabezado.franja} />
      <Linea x={M.pestana.x} y={M.pestana.y2} ancho={M.pestana.x2 - M.pestana.x} />
      <Linea x={M.divisor} y={M.pestana.y2} alto={M.encabezado.cliente - M.pestana.y2} />

      {/* El logo de RDM: es su papel, no el de ZF. */}
      <img
        src="/logo-rdm.png"
        alt="RDM Logística"
        style={{
          position: 'absolute',
          left: mm(M.logo.x),
          top: mm(M.logo.y),
          height: mm(M.logo.alto),
          width: 'auto',
        }}
      />
      <Dato x={136.4} base={29.1} rotulo="Fecha de Emisión:" valor={fecha} salto={168.6} />

      <Linea x={M.izq} y={M.encabezado.cliente} ancho={M.der - M.izq} />
      <Dato x={16.9} base={72.1} rotulo="Cliente:" valor={cliente.nombre} salto={30.8} />
      <Dato x={16.9} base={78.5} rotulo="Domicilio:" valor={cliente.domicilio} salto={35.8} />
      <Dato x={149.0} base={72.1} rotulo="CUIT:" valor={cliente.cuit} salto={159.4} />
      <Dato x={149.0} base={78.5} rotulo="Tel.:" valor={cliente.telefono} salto={159.4} />
      <Dato x={149.0} base={84.5} rotulo="Cond. IVA:" valor={cliente.condicionIva} salto={168.0} />

      {/* --------------------------------------------------------- tabla */}
      {/* El encabezado gris y los renglones. Debajo del último el papel queda
          liso, igual que el formulario: una grilla vacía hasta el pie invita a
          escribir a mano lo que el remito no dice. */}
      <div style={{ position: 'absolute', left: 0, top: mm(M.tabla.y), width: '100%' }}>
        <div style={{ position: 'relative', height: mm(M.tabla.fila) }}>
          {[
            [c0!, c1!, 'Código', 'center'],
            [c1!, c2!, 'Producto', 'left'],
            [c2!, c3!, 'Cantidad', 'center'],
          ].map(([desde, hasta, texto, alinear]) => (
            <div
              key={texto as string}
              style={{
                position: 'absolute',
                left: mm(desde as number),
                top: 0,
                width: mm((hasta as number) - (desde as number)),
                height: mm(M.tabla.fila),
                boxSizing: 'border-box',
                border: BORDE,
                background: GRIS,
                fontSize: '7pt',
                lineHeight: mm(M.tabla.fila),
                textAlign: alinear as 'left' | 'center',
                paddingLeft: alinear === 'left' ? '0.7mm' : undefined,
              }}
            >
              {texto as string}
            </div>
          ))}
        </div>

        {remito.lineas.map((l, i) => (
          <div key={i} style={{ position: 'relative', height: mm(M.tabla.fila) }}>
            {[
              [c0!, c1!, l.codigo, 'center'],
              [c1!, c2!, l.producto, 'left'],
              [c2!, c3!, cantidades[i] ?? '', 'right'],
            ].map(([desde, hasta, texto, alinear], n) => (
              <div
                key={n}
                style={{
                  position: 'absolute',
                  left: mm(desde as number),
                  top: 0,
                  width: mm((hasta as number) - (desde as number)),
                  height: mm(M.tabla.fila),
                  boxSizing: 'border-box',
                  border: BORDE,
                  borderTop: 'none',
                  fontSize: '7pt',
                  lineHeight: mm(M.tabla.fila),
                  textAlign: alinear as 'left' | 'center' | 'right',
                  padding: '0 0.8mm',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                }}
              >
                {texto as string}
              </div>
            ))}
          </div>
        ))}
      </div>

      {/* ----------------------------------------------------------- pie */}
      {/* Los tres recuadros van donde los pone el formulario, separados entre
          sí, y el medio de la hoja queda en blanco a propósito: es donde el
          depósito escribe a mano si hace falta. */}
      <Recuadro {...M.transporte} />
      <T x={16.0} base={222.5} cuerpo={6}>
        Transporte :
      </T>
      <Dato x={16.0} base={228.1} rotulo="Nombre / Razón Social:" valor={remito.transporte} salto={56.4} />
      <Dato x={161.7} base={228.1} rotulo="CUIT :" valor={remito.transporteCuit} salto={172.0} />
      <Dato x={16.0} base={234.1} rotulo="Domicilio Comercial:" valor={remito.transporteDomicilio} salto={62.0} />

      <Recuadro {...M.observaciones} />
      <T x={16.0} base={241.2} cuerpo={6}>
        Observaciones:
      </T>
      {/* Se respetan los renglones tal como se escribieron: en el formulario
          las observaciones son líneas sueltas, no un párrafo. */}
      <div
        style={{
          position: 'absolute',
          left: mm(M.obs.x),
          top: mm(topeObs),
          width: mm(M.observaciones.x2 - M.obs.x - 2),
          fontSize: `${M.obs.cuerpo}pt`,
          lineHeight: M.obs.interlinea,
          whiteSpace: 'pre-wrap',
          overflow: 'hidden',
        }}
      >
        {remito.observaciones}
      </div>

      <Recuadro {...M.facturas} />
      <Dato x={15.5} base={257.5} rotulo="Facturas Nº:" valor={remito.facturas} salto={41.0} />
    </div>
  );
}
